import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { accountApi } from "@/apis/accounts/accountApi";
import {Account, BankAccount, ConnectAccount, PaypalAccount} from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import DeleteModal from "@/components/molecules/modals/DeleteModal";
import { BankAccountInfo } from "@/components/organisms/accounts/index/BankAccountInfo";
import { StripeConnectInfo } from "@/components/organisms/accounts/index/StripeConnectInfo";
import { PaypalConnectInfo } from "@/components/organisms/accounts/index/PaypalConnectInfo";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { AccountPageLayout } from "@/components/templates/accounts/AccountPageLayout";
import { useSystemMessage } from "@/hooks/systemMessage";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import useGetPrefectures from "@/hooks/useGetPrefectures";
import useGetUserIndustries from "@/hooks/useGetUserIndustries";
import { useLoading } from "@/states/loading/operations";

const SetupPaymentPage: NextPage = () => {
  const withLoading = useLoading();
  const systemMessage = useSystemMessage();
  const { handlePageError } = useApiResponseErrorHandler();
  const { prefectures } = useGetPrefectures();
  const { userIndustries } = useGetUserIndustries();
  const [account, setAccount] = useState<Account>();
  const [connectAccount, setConnectAccount] = useState<ConnectAccount>();
  const [paypalAccount, setPaypalAccount] = useState<PaypalAccount>();
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [isOpenDeleteConnectAccountModal, setIsOpenDeleteConnectAccountModal] = useState(false);
  const [isOpenDeletePaypalAccountModal, setIsOpenDeletePaypalAccountModal] = useState(false);
  const router = useRouter();

  const getUserAccount = async () => {
    await withLoading(async () => {
      const res = await accountApi.getAccount();
      setAccount(res.data);
    });
  };
  const getUserConnectedAccount = async () => {
    await withLoading(async () => {
      const res = await accountApi.getConnectedAccount();
      setConnectAccount(res.data);
    });
  };
  const getUserPaypalAccount = async () => {
    await withLoading(async () => {
      const res = await accountApi.getPaypalAccount();
      setPaypalAccount(res.data);
    });
  };
  const getUserBankAccount = async () => {
    await withLoading(async () => {
      const res = await accountApi.getBankAccountSettings();
      setBankAccount(res.data);
    });
  };
  const createConnectAccount = async () => {
    try {
      await withLoading(async () => {
        const res = await accountApi.postConnectedAccount();
        const url =
          res && res.data && res.data.account_link
            ? new URL(res.data.account_link)
            : null;
        if (url && url.href) {
          window.open(url.href, "_blank");
        } else {
          router.reload();
        }
      });
    } catch (e) {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    }
  };
  const deleteConnectAccount = async () => {
    try {
      await withLoading(async () => {
        setIsOpenDeleteConnectAccountModal(false);
        await accountApi.deleteConnectedAccount();
        setConnectAccount({
          ...connectAccount,
          user_payment_setting: undefined,
        });
        systemMessage.success("解除しました");
      });
    } catch (e) {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    }
  };

  const createPaypalAccount = async () => {
    try {
      await withLoading(async () => {
        const res = await accountApi.postPaypalAccount();
        const url = res && res.data && res.data.account_link ? new URL(res.data.account_link) : null;
        if (url && url.href) {
          window.open(url.href, "_blank");
        } else {
          router.reload();
        }
      });
    } catch (e) {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    }
  };
  const deletePaypalAccount = async () => {
    try {
      await withLoading(async () => {
        setIsOpenDeletePaypalAccountModal(false);
        await accountApi.deletePaypalAccount();
        setConnectAccount({
          ...connectAccount,
          user_payment_setting: undefined,
        });
        systemMessage.success("解除しました");
      });
    } catch (e) {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    }
  };

  useEffect(() => {
    Promise.all([
      getUserAccount(),
      getUserConnectedAccount(),
      getUserBankAccount(),
      getUserPaypalAccount()
    ]).catch((e) => {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    });
  }, []);

  return (
    <AccountPageLayout>
      {isOpenDeleteConnectAccountModal && (
        <DeleteModal
          title="クレジットカード支払い設定"
          isOpen={isOpenDeleteConnectAccountModal}
          handleClose={() => setIsOpenDeleteConnectAccountModal(false)}
          handleSubmit={deleteConnectAccount}
          btnText="解除"
        >
          <p>クレジットカード支払い設定を解除しますか？</p>
        </DeleteModal>
      )}
      {isOpenDeletePaypalAccountModal && (
        <DeleteModal
          title={"PayPal支払い設定"}
          isOpen={isOpenDeletePaypalAccountModal}
          handleClose={() => setIsOpenDeletePaypalAccountModal(false)}
          handleSubmit={deletePaypalAccount}
          btnText={"解除"}
        >
          <p>PayPal支払い設定を解除しますか？</p>
        </DeleteModal>
      )}
      <div className="md:mt-3">
        <PageHeader title="決済設定" />
      </div>
      <div className="space-y-9 md:mt-10 md:space-y-8">
        {account && prefectures && userIndustries && connectAccount && paypalAccount && (
          <>
            <StripeConnectInfo
              connectAccount={connectAccount}
              handleCreateConnectAccount={createConnectAccount}
              handleDeleteConnectAccount={() =>
                setIsOpenDeleteConnectAccountModal(true)
              }
            />
            <PaypalConnectInfo
              paypalAccount={paypalAccount}
              handleCreatePaypalAccount={createPaypalAccount}
              handleDeletePaypalAccount={() =>
                setIsOpenDeletePaypalAccountModal(true)
              }
            />
            {bankAccount && <BankAccountInfo bankAccount={bankAccount} />}
          </>
        )}
      </div>
    </AccountPageLayout>
  );
};

export default SetupPaymentPage;
